import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { TaskFilters, TaskSort, TaskStatus, TaskPriority, ProjectFilters } from '@/types';

export interface FiltersState {
  taskFilters: TaskFilters;
  taskSort: TaskSort;
  projectFilters: ProjectFilters;
  searchQuery: string;
  dateRange: {
    start: string | null;
    end: string | null;
  };
}

const initialState: FiltersState = {
  taskFilters: {},
  taskSort: {
    field: 'dueDate',
    direction: 'asc',
  },
  projectFilters: {},
  searchQuery: '',
  dateRange: {
    start: null,
    end: null,
  },
};

const filtersSlice = createSlice({
  name: 'filters',
  initialState,
  reducers: {
    setTaskFilters: (state, action: PayloadAction<TaskFilters>) => {
      state.taskFilters = action.payload;
    },
    updateTaskFilter: (
      state,
      action: PayloadAction<Partial<TaskFilters>>
    ) => {
      state.taskFilters = { ...state.taskFilters, ...action.payload };
    },
    clearTaskFilters: (state) => {
      state.taskFilters = {};
    },
    setTaskSort: (state, action: PayloadAction<TaskSort>) => {
      state.taskSort = action.payload;
    },
    toggleTaskSortDirection: (state) => {
      state.taskSort.direction =
        state.taskSort.direction === 'asc' ? 'desc' : 'asc';
    },
    setProjectFilters: (state, action: PayloadAction<ProjectFilters>) => {
      state.projectFilters = action.payload;
    },
    updateProjectFilter: (
      state,
      action: PayloadAction<Partial<ProjectFilters>>
    ) => {
      state.projectFilters = { ...state.projectFilters, ...action.payload };
    },
    clearProjectFilters: (state) => {
      state.projectFilters = {};
    },
    setSearchQuery: (state, action: PayloadAction<string>) => {
      state.searchQuery = action.payload;
    },
    clearSearchQuery: (state) => {
      state.searchQuery = '';
    },
    setDateRange: (
      state,
      action: PayloadAction<{ start: string | null; end: string | null }>
    ) => {
      state.dateRange = action.payload;
    },
    clearAllFilters: (state) => {
      state.taskFilters = {};
      state.projectFilters = {};
      state.searchQuery = '';
      state.dateRange = { start: null, end: null };
    },
  },
});

export const {
  setTaskFilters,
  updateTaskFilter,
  clearTaskFilters,
  setTaskSort,
  toggleTaskSortDirection,
  setProjectFilters,
  updateProjectFilter,
  clearProjectFilters,
  setSearchQuery,
  clearSearchQuery,
  setDateRange,
  clearAllFilters,
} = filtersSlice.actions;

// Selectors
export const selectTaskFilters = (state: { filters: FiltersState }) =>
  state.filters.taskFilters;
export const selectTaskSort = (state: { filters: FiltersState }) =>
  state.filters.taskSort;
export const selectProjectFilters = (state: { filters: FiltersState }) =>
  state.filters.projectFilters;
export const selectSearchQuery = (state: { filters: FiltersState }) =>
  state.filters.searchQuery;
export const selectDateRange = (state: { filters: FiltersState }) =>
  state.filters.dateRange;

export default filtersSlice.reducer;
